'use client';

/**
 * PromptInputBar — pill-shaped input bar for the AI assistant V2.
 *
 * Textarea + mic + call toggle + submit. Used to also render a VOICE MODE
 * (status text + active mic + Stop pill) in place while `isCalling` was true;
 * that's gone (v3 design turn 11a) — the v3 CallScreen overlay now owns every
 * voice-call control (mute/end/CC), and page.tsx unmounts this bar entirely
 * for the duration of a call (see `statusAndInputBar`'s `!isCalling` gate),
 * so this bar only ever needs its one TEXT MODE shape.
 */

import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
} from 'react';
import { Plus, Mic, MicOff, AudioLines, ArrowUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SHADOWS } from '@/lib/design-tokens';
import type { PromptInputBarRef } from '@/types/chat';

// ── Types ─────────────────────────────────────────────────────────

interface PromptInputBarProps {
  /** Current input value (controlled) */
  value: string;
  /** Input change handler */
  onChange: (value: string) => void;
  /** Submit handler — called with trimmed text */
  onSubmit: (text: string) => void;
  /** One-shot voice-to-text transcription */
  onVoice: () => void;
  /** Toggle live voice call mode */
  onCallToggle: () => void;
  /** Whether voice recording is active */
  isRecording?: boolean;
  /** Hide the mic + call-toggle affordances (text-only surface). Used while
   *  a live-agent handoff is active (UIState.HANDOFF) — voice/call don't
   *  apply to a human-agent conversation (Decision #3, text-only handoff). */
  hideVoiceControls?: boolean;
  /** Disable all input */
  disabled?: boolean;
  /** Textarea placeholder — phase-aware copy computed by the caller (see
   *  lib/composer-placeholder.ts / page.tsx). Defaults to the plain
   *  "Ask anything" so every pre-existing call site is unaffected. */
  placeholder?: string;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────

export const PromptInputBar = memo(
  forwardRef<PromptInputBarRef, PromptInputBarProps>(function PromptInputBar(
    {
      value,
      onChange,
      onSubmit,
      onVoice,
      onCallToggle,
      isRecording = false,
      hideVoiceControls = false,
      disabled = false,
      placeholder = 'Ask anything',
      className,
    },
    ref
  ) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const hasText = value.trim().length > 0;

    // Imperative focus handle (ChatContext.promptInputRef) — startNewChat moves
    // focus here so a fresh chat is immediately typeable.
    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }));

    const handleSubmit = useCallback(() => {
      const trimmed = value.trim();
      if (!trimmed) return;
      onSubmit(trimmed);
    }, [value, onSubmit]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          handleSubmit();
        }
      },
      [handleSubmit]
    );

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
        const el = e.target;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 100)}px`;
      },
      [onChange]
    );

    return (
      <div
        className={cn(
          'mx-auto w-full max-w-3xl px-[16px] pb-[12px] pt-1 sm:px-6',
          className
        )}
      >
        <div
          className={cn(
            'flex items-center gap-2 rounded-[26px] border-[1px] border-[#eadfce] bg-white px-2 py-1.5 transition-colors duration-200 sm:px-[12px] sm:py-2'
          )}
          style={{ boxShadow: SHADOWS.input }}
        >
          {/* Plus (hidden — future extensibility) */}
          <button
            className="hidden size-7 shrink-0 items-center justify-center rounded-full text-[#9598a6]"
            disabled
            aria-label="Add attachment"
          >
            <Plus className="size-4" />
          </button>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder}
            rows={1}
            className={cn(
              'min-h-[28px] flex-1 resize-none bg-transparent px-2 py-1',
              "font-['Work_Sans'] text-[16px] font-medium text-[#31364e]",
              'placeholder:text-[15px] placeholder:text-[#b3a996]',
              'focus-visible:outline-none',
              'disabled:opacity-50'
            )}
          />

          {/* Right actions */}
          <div className="flex shrink-0 items-center gap-1">
            {/* Mic — only when no text and voice controls aren't hidden */}
            {!hasText && !hideVoiceControls && (
              <button
                onClick={onVoice}
                disabled={disabled}
                className={cn(
                  // transition-all already covers transform, so the shared
                  // press feel just adds the scale value (motion-safe-gated)
                  // rather than the full PRESSABLE class — see its doc
                  // comment in design-tokens.ts for why appending
                  // transition-transform here would be wrong (transition-all
                  // vs. transition-transform is the same one-property clash).
                  'relative flex size-8 items-center justify-center rounded-full transition-all duration-200 motion-safe:active:scale-[0.96]',
                  isRecording
                    ? 'bg-[#e36001]/10 text-[#e36001] ring-2 ring-[#e36001]/30 ring-offset-1'
                    : 'text-[#9598a6] hover:text-[#31364e]'
                )}
                aria-label={isRecording ? 'Stop recording' : 'Voice input'}
              >
                {isRecording && (
                  <span
                    className="absolute inset-0 rounded-full bg-[#e36001]/12"
                    style={{
                      animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
                    }}
                  />
                )}
                {isRecording ? (
                  <MicOff className="relative size-4" />
                ) : (
                  <Mic className="size-4" />
                )}
              </button>
            )}

            {/* Call toggle — only when no text and voice controls aren't hidden.
              Starts the call; once isCalling flips true, page.tsx unmounts
              this whole bar (see `statusAndInputBar`), so there is no
              in-place "stop" state to render here anymore — CallScreen's End
              button owns that. */}
            {!hasText && !hideVoiceControls && (
              <button
                onClick={onCallToggle}
                disabled={disabled}
                className="relative flex size-9 items-center justify-center rounded-full bg-[#1a1a1a] text-white transition-all duration-200 hover:bg-[#333] motion-safe:active:scale-[0.96]"
                aria-label="Start voice call"
              >
                <AudioLines className="relative size-[18px]" />
              </button>
            )}

            {/* Submit — solid orange circle per v3 design (not the legacy
              GRADIENTS.orange sweep). */}
            <button
              onClick={handleSubmit}
              disabled={disabled || !hasText}
              className={cn(
                'flex size-[38px] items-center justify-center rounded-full bg-[#E36001] transition-all motion-safe:active:scale-[0.96]',
                hasText ? 'text-white' : 'text-white opacity-50'
              )}
              aria-label="Send message"
            >
              <ArrowUp className="size-[18px]" />
            </button>
          </div>
        </div>
      </div>
    );
  })
);
